import { useCallback, useRef } from "react";
import { newIdempotencyKey } from "./idempotency";

// One idempotency key per user intent (see idempotency.ts), for forms and
// confirm dialogs. `keyFor(signature)` returns the same key for as long as
// the request is unchanged — so a retry after a timeout, a double-click or
// a step-up prompt all reuse it and the server replays instead of acting
// twice (G-21: campaign_commands.go runCampaignCommand,
// publisher_commands.go runPublisherCommand). A different signature (the
// user changed the amount, the name, the dates) mints a new key. Call
// `reset()` once the intent has succeeded: the next identical request is
// a new intent (pause → resume → pause must pause again, not replay).

export interface IntentKey {
  keyFor: (signature: string) => string;
  reset: () => void;
}

export function useIntentKey(): IntentKey {
  const current = useRef<{ sig: string; key: string } | null>(null);
  const keyFor = useCallback((sig: string) => {
    if (!current.current || current.current.sig !== sig) current.current = { sig, key: newIdempotencyKey() };
    return current.current.key;
  }, []);
  const reset = useCallback(() => {
    current.current = null;
  }, []);
  return { keyFor, reset };
}
